import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Row, Col, Space, Button, Card } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ArrayField from '../../../../components/fields/ArrayField';
import FormItem from '../../../../components/fields/FormItem';
import ImageUploadField from '../../../../components/fields/ImageUploadField';
import TranslatedMarkdownField from '../../../../components/fields/TranslatedMarkdownField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import { allowedExtensions } from '../../../../utilities/extensions';

type AdditionalDetailItemProps = {
    name: string;
    remove?: (index: number) => void;
    index?: number;
    disabled?: boolean;
};

const AdditionalDetailItem = ({ name, index, remove, disabled = false }: AdditionalDetailItemProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const onDeleteAdditional = useCallback(() => {
        remove(index);
    }, [index, remove]);

    return (
        <Card
            extra={!disabled && <Button icon={<DeleteOutlined />} onClick={onDeleteAdditional} type="link" danger />}
            title={t('variantConfiguratorDetails:sections.packages.details', { number: index + 1 })}
            type="inner"
        >
            <Row gutter={14}>
                <Col lg={8} xs={24}>
                    <ImageUploadField
                        {...t('variantConfiguratorDetails:fields.packages.additionalDetailImage', {
                            returnObjects: true,
                        })}
                        disabled={disabled}
                        extensions={allowedExtensions.image}
                        height={200}
                        imageFit="contain"
                        name={`${name}image`}
                    />
                </Col>
                <Col lg={16} xs={24}>
                    <TranslatedTextAreaField
                        {...t('variantConfiguratorDetails:fields.packages.additionalDetailTitle', {
                            returnObjects: true,
                        })}
                        disabled={disabled}
                        name={`${name}title`}
                    />
                    <TranslatedMarkdownField
                        {...t('variantConfiguratorDetails:fields.packages.additionalDetailDescription', {
                            returnObjects: true,
                        })}
                        disabled={disabled}
                        name={`${name}description`}
                        withContentRefinement
                    />
                </Col>
            </Row>
        </Card>
    );
};

type AdditionalSettingProps = {
    name: string;
    disabled?: boolean;
};

const PackageAdditionalDetailFieldGroup = ({ name, disabled = false }: AdditionalSettingProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    return (
        <FormItem label={t('variantConfiguratorDetails:fields.packages.additionalDetails.label')}>
            <ArrayField
                name={name}
                render={({ push, remove }, additionalDetails) => (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {additionalDetails?.map((detail, index) => (
                            <AdditionalDetailItem
                                key={index.toString()}
                                disabled={disabled}
                                index={index}
                                name={`${name}[${index}]`}
                                remove={remove}
                            />
                        ))}
                        {!disabled && (
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => push({ title: '', description: '' })}
                                style={{ marginTop: 10 }}
                                type="link"
                            >
                                {t('variantConfiguratorDetails:actions.packages.addAdditionalDetail')}
                            </Button>
                        )}
                    </Space>
                )}
            />
        </FormItem>
    );
};

export default PackageAdditionalDetailFieldGroup;
